import type { Post } from '../payload-types'
import { hostOf } from '@/lib/sourceClass'
import { CHIP, CHIP_ACCENT, SECTION_EYEBROW, TAIL_RULE } from './chip'

type Source = NonNullable<Post['sources']>[number]

/**
 * Il dominio senza `www.`: è l'etichetta del chip.
 *
 * SI RICAVA DALL'URL, non dal campo `host` salvato, che in tabella è incoerente: le righe dei post
 * 40-43 hanno `www.comune.santafiora.gr.it`, quelle dei post 57-58 `comune.santafiora.gr.it`. Su
 * una riga per fonte quella differenza era invisibile; su una fila di chip diventa una fila che non
 * torna, con lo stesso ente scritto in due modi nello stesso articolo. `hostOf` toglie il `www.` e
 * abbassa le maiuscole; il campo salvato resta come ripiego se l'URL non si parsa.
 */
const hostLabel = (s: Source): string => {
  const h = hostOf(String(s.url ?? '')) || String(s.host ?? '').replace(/^www\./i, '').toLowerCase()
  return h || String(s.url ?? '')
}

/**
 * Quante informazioni sono state estratte da questa fonte: il numero che il chip stampa e, da quando
 * gli apici non ci sono più, l'unica misura del peso di una fonte nell'articolo. Il campo è
 * `number | null | undefined` in Payload (le righe importate a mano non lo hanno): qui zero, che è
 * anche il valore giusto per l'ordinamento.
 */
const factsOf = (s: Source): number => (typeof s.factCount === 'number' ? s.factCount : 0)

/**
 * ULTIMO segmento di path, per distinguere due chip dello stesso dominio.
 *
 * Serve perché l'etichetta del chip è l'host: due pagine dello stesso sito — che nell'elenco
 * verticale si distinguevano per titolo — diventerebbero due chip identici, e il lettore non
 * potrebbe dire se sono due fonti diverse o la stessa contata due volte. Oggi in tabella non
 * succede (30 righe, nessun host ripetuto dentro lo stesso articolo), quindi questo ramo di norma
 * non si accende: sta qui perché la fila di chip è l'unico layout in cui quel caso mente.
 *
 * L'ULTIMO E NON IL PRIMO, che era la scelta ovvia e non distingueva niente: provato sui due
 * articoli di it.wikipedia.org, il primo segmento è `/wiki` per entrambi — cioè due chip identici
 * di nuovo, esattamente il caso che questa funzione esiste per chiudere. L'ultimo segmento è lo
 * slug della pagina, che è la parte che cambia.
 */
const pathHint = (url: string): string => {
  try {
    const segs = new URL(url).pathname.split('/').filter(Boolean)
    const seg = (segs[segs.length - 1] ?? '').replace(/\.(?:html?|php|aspx?)$/i, '')
    return seg ? `/${seg.length > 14 ? `${seg.slice(0, 13)}…` : seg}` : ''
  } catch {
    return ''
  }
}

/**
 * Le fonti dell'articolo, renderizzate in coda al corpo.
 *
 * Il campo `posts.sources` esisteva da sempre — raccolto dal generator, salvato dal worker,
 * persistito — e non compariva in nessun file di `src/app`. La tracciabilità che mancava al
 * lettore non richiedeva né una migrazione né una chiamata a pagamento: richiedeva di
 * renderizzare dati che erano già in tabella.
 *
 * VIVE FUORI DAL CORPO, e non è un dettaglio di layout: un LLM non può scrivere né alterare
 * questi elementi, e non passano da convertHTMLToLexical — che, verificato, scarta rel="nofollow"
 * dai link del corpo. Un blocco fonti scritto dentro l'articolo sarebbe l'ennesima cosa che il
 * modello può inventare; questo no.
 *
 * SE NON CI SONO FONTI IL COMPONENTE NON SPARISCE: lo dice. Un'assenza silenziosa è
 * indistinguibile da una pagina ben sorgentata, ed è esattamente la confusione che ha permesso a
 * 34 articoli senza una riga in posts_sources di sembrare uguali a uno documentato.
 *
 * CHIP IN FILA, NON UNA RIGA PER FONTE. Il post 58 ne ha 8: erano 8 righe più il titolo di sezione,
 * cioè un blocco alto quanto un capitolo dell'articolo per una nota di servizio che si consulta.
 * In fila stanno in 2-3 righe di testo. Il prezzo è che l'etichetta deve essere corta, quindi è
 * l'host — che è anche la risposta alla domanda che le fonti servono a chiudere («chi lo dice»),
 * mentre il titolo della pagina, che su metà delle righe in tabella è comunque `null`, resta nel
 * `title` del link e nel nome accessibile.
 *
 * IL BADGE «fonte istituzionale» DIVENTA COLORE, e l'informazione non si perde: ripetuto per
 * esteso dentro ogni chip valeva da solo più dell'host, cioè avrebbe riportato la fila all'altezza
 * dell'elenco che sostituisce. Il chip istituzionale prende la tinta d'accento (per-tenant, come
 * <PracticalBox>), la legenda lo dice una volta accanto al titolo di sezione, e chi usa uno screen
 * reader continua a sentire «fonte istituzionale» su ogni singolo link: il colore non è l'unico
 * veicolo del dato, che è il difetto tipico di questa sostituzione.
 *
 * E QUESTA FILA È L'UNICA TRACCIABILITÀ CHE LA PAGINA MOSTRA. Per due commit ogni chip portava un
 * numero, perché il corpo lo citava con un apice; gli apici sono spariti — venti-trenta rimandi in
 * un articolo erano il rumore che questo blocco esiste per evitare (src/lib/citations.ts) — e con
 * loro il numero e l'ancora. Quello che il lettore trova qui, per ogni fonte, è chi è e quante
 * informazioni ne sono state estratte: le due cose che rispondono alla domanda per cui l'elenco è
 * in pagina.
 */
export default function SourcesBlock({ sources }: { sources?: Source[] | null }) {
  // LA FILA È ORDINATA PER NUMERO DI DATI, DECRESCENTE, ed è l'unico ordine che questo layout può
  // permettersi: il numero è scritto dentro ogni chip, quindi una fila che non lo rispetta si legge
  // come un elenco messo in disordine («7 dati», «17 dati», «5 dati» — è la sequenza del post 62).
  // La prima riga, che è l'unica che tutti leggono, porta le fonti da cui viene la maggior parte
  // dell'articolo.
  //
  // SI ORDINA QUI E NON SOLO NEL WORKER, e la ridondanza è voluta: il worker salva già in questo
  // ordine (src/worker/index.ts), ma i pezzi generati nei due giorni degli apici sono salvati
  // nell'ordine di PRIMA CITAZIONE nel corpo — un ordine che serviva ai numeri e che senza di loro
  // non dice più niente. È la stessa scelta dell'apice scartato in resa: si ripara ciò che è già in
  // tabella dove non costa un update di Posts. `filter` restituisce già un array nuovo, quindi
  // `sort` non tocca l'oggetto che il post porta in stato React (lo scroll infinito lo rimonta).
  //
  // Le istituzionali NON risalgono in testa: restano in tinta d'accento, che è come si riconoscevano
  // comunque, e un ordinamento per classe di fiducia metterebbe davanti un sito con zero dati.
  const list = (sources ?? []).filter((s) => s?.url).sort((a, b) => factsOf(b) - factsOf(a))
  const hosts = list.map(hostLabel)
  const repeated = new Set(hosts.filter((h, i) => hosts.indexOf(h) !== i))
  const hasInstitutional = list.some((s) => s.trustClass === 'istituzionale')

  return (
    // `aria-label` e non l'`aria-labelledby="fonti"` di prima: lo scroll infinito monta N
    // <ArticleView> nella stessa pagina, quindi quell'id era duplicato N volte e il nome della
    // regione andava al primo articolo del DOM (è il difetto che <PracticalBox> documenta e che
    // evita alla sua maniera). Nessun link punta a `#fonti`: verificato su tutto src/.
    // NIENTE MARGINE QUI: la distanza fra i blocchi della coda la tiene il contenitore in
    // <ArticleView> (vedi `TAIL_RULE`).
    <section className={TAIL_RULE} aria-label="Fonti">
      {/* Titolo e legenda sulla stessa riga: la legenda esiste per spiegare la tinta dei chip
          istituzionali, e messa sotto la fila si mangerebbe una delle righe che questo layout ha
          appena risparmiato. Su mobile va a capo da sola. */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-4">
        <h2 className={SECTION_EYEBROW}>Fonti</h2>
        {hasInstitutional ? <p className="text-xs text-zinc-500">In evidenza le fonti istituzionali</p> : null}
      </div>

      {list.length ? (
        // `role="list"` esplicito: con `display: flex` Safari/VoiceOver toglie all'elemento il ruolo
        // di lista, e chi ascolta perde il «7 elementi» che dice quante fonti ha questa pagina.
        <ul role="list" className="mt-3 flex flex-wrap gap-2">
          {list.map((s, i) => {
            const institutional = s.trustClass === 'istituzionale'
            const facts = factsOf(s)
            const label = `${hosts[i]}${repeated.has(hosts[i]) ? pathHint(String(s.url)) : ''}`

            return (
              <li key={s.id ?? `${s.url}-${i}`}>
                <a
                  href={s.url as string}
                  rel="nofollow noopener external"
                  target="_blank"
                  title={s.title || undefined}
                  className={institutional ? CHIP_ACCENT : CHIP}
                >
                  {/* Il troncamento è in JS e non in CSS (`truncate` in un flex item che il
                      genitore non vincola non tronca nulla): l'host misura al massimo 28
                      caratteri, quindi taglia solo il ripiego sull'URL intero. */}
                  {label.length > 44 ? `${label.slice(0, 43)}…` : label}
                  {institutional ? <span className="sr-only"> (fonte istituzionale)</span> : null}
                  {s.title ? <span className="sr-only">, {s.title}</span> : null}
                  {/* QUANTE INFORMAZIONI VENGONO DA QUI, ed è il dato che ha preso il posto degli
                      apici: dice quanto pesa questa fonte nell'articolo senza chiedere al lettore di
                      inseguire un numero riga per riga. Zero non si stampa — una fonte consultata e
                      senza un dato estratto non deve sembrare una fonte che ne ha dati zero per
                      errore — e resta in fila perché è stata comunque letta.
                      Non è `aria-hidden`: a differenza del vecchio numero questa è informazione, non
                      un ponte visivo, e chi ascolta la sente dopo l'host. */}
                  {facts > 0 ? (
                    <span className={institutional ? 'font-normal text-accent-strong/80' : 'font-normal text-zinc-500'}>
                      {facts} {facts === 1 ? 'dato' : 'dati'}
                    </span>
                  ) : null}
                </a>
              </li>
            )
          })}
        </ul>
      ) : (
        <p className="mt-3 text-sm text-zinc-500">Questa pagina non ha fonti registrate.</p>
      )}
    </section>
  )
}
